import { useEffect, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ChannelType as ChannelType } from 'shared/types/graphql';

export const getChannelUnreadMessageCount = 'getChannelUnreadMessageCount';

const CHANNEL_UNREAD_MESSAGE_COUNT = gql`
  query ${getChannelUnreadMessageCount} {
    channelUnreadMessageCount {
      total
      unreadChannels {
        channelId
        channelLastMessageIndex
        lastConsumedMessageIndex
        unreadMessageCount
      }
      client {
        total
      }
      team {
        total
      }
    }
  }
`;

type UnreadMessageChannel = {
  channelId: string;
  channelLastMessageIndex: number;
  lastConsumedMessageIndex: number;
  unreadMessageCount: number;
  channelType: ChannelType;
};

type ChannelCountResult = {
  total: number;
  unreadChannels: UnreadMessageChannel[];
};

interface IUnreadMessageCount {
  data: {
    total: number;
    unreadChannelArray?: string[];
    unreadChannelMap: { [channelId: string]: { count: number } };
    clientTotal: number;
    teamTotal: number;
  };
  refreshData: () => void;
}

const useUnreadMessageCount = (): IUnreadMessageCount => {
  const { currentClinicId } = useClinicUser();

  const { data, refetch, client } = useQuery<{
    channelUnreadMessageCount: {
      total: number;
      unreadChannels: UnreadMessageChannel[];
      client: Omit<ChannelCountResult, 'unreadChannels'>;
      team: Omit<ChannelCountResult, 'unreadChannels'>;
    };
  }>(CHANNEL_UNREAD_MESSAGE_COUNT, {
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      /**
       * Explicitly invalidate the cache when results return from the network
       * https://github.com/apollographql/apollo-client/issues/7994
       */
      client.cache.modify({
        id: client.cache.identify({ __typename: 'ChannelUnreadMessageCountResult' }),
        fields(fieldValue, details) {
          return details.INVALIDATE;
        },
      });
    },
  });

  /**
   * Refetch notification count when user switches to another clinic
   */
  useEffect(() => {
    if (refetch && currentClinicId) {
      refetch();
    }
  }, [refetch, currentClinicId]);

  const unreadMessageCountByChannel = useMemo(() => {
    if (!data?.channelUnreadMessageCount?.total)
      return { total: 0, unreadChannelMap: {}, clientTotal: 0, teamTotal: 0 };
    const { total, unreadChannels, client, team } = data?.channelUnreadMessageCount;

    const channelMap = unreadChannels.reduce((prev, { channelId, unreadMessageCount }) => {
      prev[channelId] = { count: unreadMessageCount };
      return prev;
    }, {} as Record<string, { count: number }>);

    const channelArray = Object.entries(channelMap)
      .filter(([, { count }]) => count > 0)
      .map(([id]) => id);

    return {
      total,
      unreadChannels,
      unreadChannelMap: channelMap,
      unreadChannelArray: channelArray,
      clientTotal: client.total,
      teamTotal: team.total,
    };
  }, [data]);

  return { data: unreadMessageCountByChannel, refreshData: refetch };
};

export default useUnreadMessageCount;
